<script lang="ts">
    import { generateIcon } from "@components/material/Icon.module"
    import { fade, scale } from "svelte/transition"
    import { closeHelp, openHelp, _open, _location } from "@components/help/Help.module"
    import { HELP, type HelpDocPaths, type HelpSection } from "virtual:documentation"
    import Icon from "@components/material/Icon.svelte"
    import { AddEscapeStack } from "@utils/underhood/escape-key"
    import { onDestroy } from "svelte"
    import { delayedClick } from "@utils/helpers/delayed-click"

    export const sectionHelpMap = HELP.reduce(
        (map, section) => map.set(section.slug, section),
        new Map<string, HelpSection>(),
    )

    /**
     * Hides all nodes that don't have anything to do with supplied ID
     * @param node
     * @param param1
     */
    function hideEverythingExcept(node: HTMLElement, { id = "" }) {
        console.log("Trimming using id ", id)
        if (id != "") {
            let visible = false
            let depth = 1 // nested headings need to show up too
            let found = false
            ;([...node.children] as HTMLElement[]).forEach((ele) => {
                if (ele.id == id) {
                    // We hit the target
                    visible = true
                    found = true
                    depth = getHeadingDepth(ele)
                } else if (ele.id) {
                    if (getHeadingDepth(ele) <= depth) visible = false
                    // Ended on another ID
                }

                if (visible) {
                    ele.style.removeProperty("display")
                } else {
                    ele.style.display = "none"
                }
            })

            if (!found)
                node.innerHTML =
                    "<h3>Whoops!</h3><p>The content for this help section wasn't found!</p>"
        }

        return {
            destroy() {
                ;([...node.children] as HTMLElement[]).forEach((element) => {
                    element.style.removeProperty("display")
                })
            },
        }
    }

    function getHeadingDepth(element: HTMLElement) {
        const match = element.tagName.match(/[0-9]+/)
        return match ? Number(match[0]) : Number.POSITIVE_INFINITY
    }

    /**
     * The help package supplies icons in the format
     * of `((some-icon))`
     * @param iconName
     */
    function replaceIcons(content: string) {
        return content.replace(/\(\(.+?\)\)/g, (str) => {
            const name = str.match(/^\(\((.+?)\)\)$/)?.[1]
            if (!name) return "[Icon match failed: " + name + "]"

            const rtn = generateIcon(name)
            if (!rtn) return "[Icon not found: " + name + "]"

            return rtn
        })
    }

    function processLinks(node: HTMLElement) {
        const links = [...node.querySelectorAll("a")]
        links.forEach((link) => {
            link.target = "_blank"
            link.classList.add("link", "underline")
        })

        const helpLinks = links.filter((link) => !link.getAttribute("href")?.startsWith("http"))
        helpLinks.forEach(function (a) {
            a.addEventListener("click", function (e) {
                e.preventDefault()
                openHelp(this.getAttribute("href") as HelpDocPaths)
            })
        })
    }

    let helpContentElement: HTMLElement | null = null

    function scrollHelpContentFromRow(event: WheelEvent) {
        if (!helpContentElement || event.ctrlKey) return

        const multiplier =
            event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? helpContentElement.clientHeight : 1
        const top = event.deltaY * multiplier
        const left = event.deltaX * multiplier

        if (!top && !left) return

        event.preventDefault()
        helpContentElement.scrollTop += top
        helpContentElement.scrollLeft += left
    }

    let removeEscapeStack: (() => void) | null = null

    function syncEscapeStack(open: boolean) {
        if (open && !removeEscapeStack) {
            removeEscapeStack = AddEscapeStack({
                name: "Help Guide",
                fn: closeHelp,
            })
        }

        if (!open && removeEscapeStack) {
            removeEscapeStack()
            removeEscapeStack = null
        }
    }

    onDestroy(() => {
        if (removeEscapeStack) removeEscapeStack()
    })

    function closeHelpFromOutside() {
        closeHelp()
    }

    $: currentSection = $_location?.page ? sectionHelpMap.get($_location.page) : null
    $: syncEscapeStack($_open)
</script>

{#if $_open}
    <div
        id="help-container"
        class="help-guide-overlay"
        transition:fade|global={{ duration: 200 }}
        use:delayedClick={{ callback: closeHelpFromOutside }}
    >
        <section
            id="help"
            class="help-guide"
            role="dialog"
            aria-modal="true"
            aria-labelledby="help-title"
            transition:scale|global={{ duration: 180, start: 0.98 }}
        >
            <header class="help-guide__header">
                <div class="help-guide__title-group">
                    <span class="help-guide__icon" aria-hidden="true">
                        <Icon icon="help-solid" />
                    </span>
                    <div class="help-guide__title-copy">
                        <p class="help-guide__eyebrow">Guide</p>
                        <h2 id="help-title">Help Guide</h2>
                    </div>
                </div>
                <button
                    class="help-guide__close"
                    type="button"
                    on:click={() => closeHelp()}
                    aria-label="Close help guide"
                >
                    <Icon icon="close" />
                </button>
            </header>

            <nav class="help-guide__breadcrumbs" aria-label="Help guide location">
                {#if $_location?.page}
                    <button class="help-guide__breadcrumb" type="button" on:click={() => openHelp()}
                        >Help Guide</button
                    >
                {:else}
                    <span class="help-guide__breadcrumb-current">Help Guide</span>
                {/if}
                {#if $_location?.page && currentSection}
                    <span class="help-guide__breadcrumb-separator" aria-hidden="true">/</span>
                    {#if $_location.id}
                        <button
                            class="help-guide__breadcrumb"
                            type="button"
                            on:click={() => openHelp($_location.page)}
                        >
                            {currentSection.title}
                        </button>
                    {:else}
                        <span class="help-guide__breadcrumb-current">{currentSection.title}</span>
                    {/if}
                {:else}
                    <!-- Page not found! -->
                {/if}
            </nav>

            <div
                id="help-content"
                class="help-guide__content normal-rendering"
                bind:this={helpContentElement}
            >
                {#if $_location}
                    {#key $_location}
                        <div use:hideEverythingExcept={{ id: $_location.id }} use:processLinks>
                            {#if currentSection}
                                {@html replaceIcons(currentSection.content)}
                            {/if}
                        </div>
                    {/key}
                {:else}
                    <!-- INDEXES -->
                    <div class="help-guide__index">
                        <ol
                            class="help-guide__index-list"
                            aria-label="Help Guide sections"
                            on:wheel|nonpassive={scrollHelpContentFromRow}
                        >
                            {#each HELP as { index, title, slug }}
                                <li>
                                    <a
                                        href={`#${slug}`}
                                        class="help-guide__index-item"
                                        on:click|preventDefault={() => openHelp(slug)}
                                    >
                                        <span class="help-guide__index-number">{index}</span>
                                        <span class="help-guide__index-title">{title}</span>
                                        <Icon icon="arrow-right" />
                                    </a>
                                </li>
                            {/each}
                        </ol>
                    </div>
                {/if}
            </div>
        </section>
    </div>
{/if}

<style>
    .help-guide-overlay {
        --help-guide-edge-padding: 1rem;

        position: fixed;
        inset: 0;
        z-index: var(--z-popup-top-layer, 11000);
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        padding: calc(var(--safe-area-inset-top, 0rem) + var(--help-guide-edge-padding))
            calc(var(--safe-area-inset-right, 0rem) + var(--help-guide-edge-padding))
            calc(var(--safe-area-inset-bottom, 0rem) + var(--help-guide-edge-padding))
            calc(var(--safe-area-inset-left, 0rem) + var(--help-guide-edge-padding));
        background: rgba(255, 255, 255, 0.56);
        backdrop-filter: blur(8px);
        color: var(--color-grey-900);
        cursor: pointer;
        font-family:
            Inter,
            ui-sans-serif,
            system-ui,
            -apple-system,
            BlinkMacSystemFont,
            "Segoe UI",
            sans-serif;
    }

    .help-guide {
        position: relative;
        display: flex;
        flex-direction: column;
        width: min(100%, 46rem);
        max-height: 100%;
        min-height: min(34rem, 100%);
        overflow: hidden;
        border: 1px solid var(--color-purple-100);
        border-radius: 1rem;
        background: var(--color-grey-0);
        color: var(--color-grey-900);
        box-shadow: none;
        cursor: auto;
    }

    .help-guide__header {
        flex: 0 0 auto;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        width: 100%;
        min-width: 0;
        padding: 1rem 1rem 0.9rem;
        border-bottom: 1px solid var(--color-purple-100);
        background: var(--color-grey-0);
    }

    .help-guide__title-group {
        display: flex;
        flex: 1 1 auto;
        min-width: 0;
        align-items: center;
        gap: 0.75rem;
    }

    .help-guide__icon {
        display: inline-flex;
        flex: 0 0 auto;
        align-items: center;
        justify-content: center;
        width: 2.35rem;
        height: 2.35rem;
        border: 1px solid var(--color-purple-100);
        border-radius: 0.75rem;
        background: var(--color-purple-50);
        color: var(--color-purple-700);
    }

    .help-guide__icon :global(svg) {
        width: 1.25rem;
        height: 1.25rem;
    }

    .help-guide__title-copy {
        min-width: 0;
    }

    .help-guide__eyebrow {
        margin: 0 0 0.1rem;
        color: var(--color-purple-700);
        font-size: 0.76rem;
        font-weight: 700;
        letter-spacing: 0;
        line-height: 1.1;
        text-transform: uppercase;
    }

    #help-title {
        margin: 0;
        color: var(--color-grey-900);
        font-family: Lora, ui-serif, Georgia, Cambria, "Times New Roman", Times, serif;
        font-size: 1.45rem;
        font-weight: 500;
        letter-spacing: 0;
        line-height: 1.2;
    }

    .help-guide__close {
        appearance: none;
        display: inline-flex;
        flex: 0 0 auto;
        align-items: center;
        justify-content: center;
        margin-left: auto;
        width: 2.35rem;
        height: 2.35rem;
        padding: 0;
        border: 1px solid var(--color-purple-100);
        border-radius: 0.75rem;
        background: var(--color-grey-0);
        color: var(--color-purple-700);
        cursor: pointer;
        transition:
            background-color 0.15s ease,
            border-color 0.15s ease,
            color 0.15s ease;
    }

    .help-guide__close:hover {
        border-color: var(--color-purple-200);
        background: var(--color-purple-50);
        color: var(--color-purple-800);
    }

    .help-guide__close:focus-visible,
    .help-guide__breadcrumb:focus-visible,
    .help-guide__index-item:focus-visible {
        outline: 2px solid var(--color-gold-400);
        outline-offset: 2px;
    }

    .help-guide__close :global(svg) {
        width: 1rem;
        height: 1rem;
    }

    .help-guide__breadcrumbs {
        display: flex;
        min-width: 0;
        align-items: center;
        gap: 0.45rem;
        padding: 0.7rem 1rem;
        border-bottom: 1px solid var(--color-grey-100);
        background: var(--color-grey-50);
        color: var(--color-grey-600);
        font-size: 0.9rem;
        line-height: 1.35;
    }

    .help-guide__breadcrumb,
    .help-guide__breadcrumb-current {
        min-width: 0;
        border: 0;
        border-radius: 0.4rem;
        background: transparent;
        color: var(--color-grey-600);
        font: inherit;
        letter-spacing: 0;
    }

    .help-guide__breadcrumb {
        padding: 0.15rem 0.25rem;
        color: var(--color-purple-700);
        cursor: pointer;
        text-decoration: underline;
        text-decoration-color: color-mix(in srgb, var(--color-purple-700) 35%, transparent);
        text-underline-offset: 0.18em;
    }

    .help-guide__breadcrumb:hover {
        background: var(--color-purple-50);
        color: var(--color-purple-800);
        text-decoration-color: currentColor;
    }

    .help-guide__breadcrumb-current {
        overflow: hidden;
        padding: 0.15rem 0;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .help-guide__breadcrumb-separator {
        color: var(--color-grey-300);
    }

    .help-guide__content {
        flex: 1 1 auto;
        min-height: 0;
        overflow-y: auto;
        overscroll-behavior: contain;
        touch-action: pan-y;
        -webkit-overflow-scrolling: touch;
        background: var(--color-grey-0);
    }

    .help-guide__content > div {
        width: 100%;
        min-height: 100%;
        max-width: 42rem;
        margin: 0 auto;
        padding: 1rem 1.25rem 1.35rem;
    }

    .help-guide__index-list {
        overflow: hidden;
        margin: 0;
        padding: 0;
        border: 1px solid var(--color-purple-100);
        border-radius: 0.75rem;
        background: var(--color-grey-0);
        list-style: none;
    }

    .help-guide__index-list li {
        margin: 0;
        padding: 0;
    }

    .help-guide__index-list li + li {
        border-top: 1px solid var(--color-grey-100);
    }

    .help-guide__index-item {
        appearance: none;
        display: flex;
        align-items: center;
        width: 100%;
        gap: 0.75rem;
        padding: 0.85rem 1rem;
        border: 0;
        background: var(--color-grey-0);
        color: var(--color-grey-900);
        cursor: pointer;
        font: inherit;
        letter-spacing: 0;
        text-align: left;
        text-decoration: none;
        overscroll-behavior: auto;
        touch-action: pan-y;
        transition:
            background-color 0.15s ease,
            color 0.15s ease;
    }

    .help-guide__index-item :global(*) {
        overscroll-behavior: auto;
        touch-action: pan-y;
    }

    .help-guide__index-item:hover {
        background: var(--color-purple-50);
        color: var(--color-purple-800);
    }

    .help-guide__index-number {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 2rem;
        height: 2rem;
        padding: 0 0.35rem;
        border: 1px solid var(--color-purple-100);
        border-radius: 0.6rem;
        background: var(--color-purple-50);
        color: var(--color-purple-700);
        font-size: 0.78rem;
        font-weight: 700;
        line-height: 1;
    }

    .help-guide__index-title {
        flex: 1 1 auto;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .help-guide__index-item :global(svg) {
        width: 0.72rem;
        height: 0.72rem;
        color: var(--color-grey-400);
    }

    .help-guide__content :global(h1),
    .help-guide__content :global(h2),
    .help-guide__content :global(h3),
    .help-guide__content :global(h4),
    .help-guide__content :global(h5),
    .help-guide__content :global(h6) {
        color: var(--color-grey-900);
        font-family: Lora, ui-serif, Georgia, Cambria, "Times New Roman", Times, serif;
        font-weight: 500;
        letter-spacing: 0;
        line-height: 1.25;
    }

    .help-guide__content :global(h1),
    .help-guide__content :global(h2) {
        font-size: 1.3rem;
    }

    .help-guide__content :global(p),
    .help-guide__content :global(ul),
    .help-guide__content :global(ol) {
        color: var(--color-grey-600);
        font-size: 1rem;
        line-height: 1.68;
    }

    .help-guide__content :global(a.link) {
        color: var(--color-purple-700);
        text-decoration-color: color-mix(in srgb, var(--color-purple-700) 40%, transparent);
        text-underline-offset: 0.18em;
    }

    .help-guide__content :global(a.link:hover) {
        color: var(--color-purple-800);
        text-decoration-color: currentColor;
    }

    .help-guide__content :global(svg[data-icon-name]) {
        display: inline-block;
        width: 1.5em;
        height: 1.5em;
        margin: 0 0.05em;
        padding: 0.22rem;
        border: 1px solid var(--color-grey-100);
        border-radius: 0.35rem;
        background: var(--color-grey-50);
        color: var(--color-purple-700);
        vertical-align: middle;
    }

    .help-guide__content :global(code) {
        display: inline-block;
        padding: 0 0.3rem;
        border: 1px solid var(--color-grey-100);
        border-radius: 0.3rem;
        background: var(--color-grey-50);
        color: var(--color-grey-800);
        font-family: inherit;
        font-size: 0.9em;
        font-weight: 600;
    }

    :global(.darkmode) .help-guide-overlay {
        background: rgba(0, 0, 0, 0.6);
        color: var(--color-grey-50);
    }

    :global(.darkmode) .help-guide,
    :global(.darkmode) .help-guide__header,
    :global(.darkmode) .help-guide__content,
    :global(.darkmode) .help-guide__index-list,
    :global(.darkmode) .help-guide__index-item {
        border-color: var(--color-grey-700);
        background: var(--color-grey-900);
        color: var(--color-grey-50);
    }

    :global(.darkmode) .help-guide__breadcrumbs {
        border-color: var(--color-grey-700);
        background: var(--color-grey-800);
        color: var(--color-grey-300);
    }

    :global(.darkmode) .help-guide__icon,
    :global(.darkmode) .help-guide__index-number {
        border-color: var(--color-grey-700);
        background: var(--color-grey-800);
        color: var(--color-purple-100);
    }

    :global(.darkmode) .help-guide__eyebrow,
    :global(.darkmode) .help-guide__breadcrumb {
        color: var(--color-purple-100);
    }

    :global(.darkmode) #help-title,
    :global(.darkmode) .help-guide__content :global(h1),
    :global(.darkmode) .help-guide__content :global(h2),
    :global(.darkmode) .help-guide__content :global(h3),
    :global(.darkmode) .help-guide__content :global(h4),
    :global(.darkmode) .help-guide__content :global(h5),
    :global(.darkmode) .help-guide__content :global(h6) {
        color: var(--color-grey-50);
    }

    :global(.darkmode) .help-guide__close {
        border-color: var(--color-grey-700);
        background: var(--color-grey-900);
        color: var(--color-purple-100);
    }

    :global(.darkmode) .help-guide__close:hover,
    :global(.darkmode) .help-guide__breadcrumb:hover,
    :global(.darkmode) .help-guide__index-item:hover {
        border-color: var(--color-grey-600);
        background: var(--color-grey-800);
        color: var(--color-purple-100);
    }

    :global(.darkmode) .help-guide__index-list li + li {
        border-color: var(--color-grey-700);
    }

    :global(.darkmode) .help-guide__breadcrumb-current,
    :global(.darkmode) .help-guide__content :global(p),
    :global(.darkmode) .help-guide__content :global(ul),
    :global(.darkmode) .help-guide__content :global(ol) {
        color: var(--color-grey-200);
    }

    :global(.darkmode) .help-guide__content :global(a.link) {
        color: var(--color-purple-100);
    }

    :global(.darkmode) .help-guide__content :global(svg[data-icon-name]),
    :global(.darkmode) .help-guide__content :global(code) {
        border-color: var(--color-grey-700);
        background: var(--color-grey-800);
        color: var(--color-purple-100);
    }

    @media (max-width: 600px) {
        .help-guide-overlay {
            --help-guide-edge-padding: 0.75rem;

            align-items: stretch;
        }

        .help-guide {
            min-height: 0;
            border-radius: 0.85rem;
        }

        .help-guide__header {
            align-items: flex-start;
            padding: 0.85rem;
        }

        #help-title {
            font-size: 1.3rem;
        }

        .help-guide__breadcrumbs {
            padding: 0.65rem 0.85rem;
        }

        .help-guide__content > div {
            padding: 0.9rem 0.95rem 1.15rem;
        }

        .help-guide__index-item {
            padding: 0.8rem 0.85rem;
        }
    }
</style>
